import { User } from '@/types/core/User';
import {
  Box,
  Flex,
  Link,
  List,
  ListItem,
  SimpleGrid,
  Tag,
  TagLabel,
  Text,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import userTranslations from '@/translations/user.fr.yml';
import SimpleCard from '@components/presentational/Cards/SimpleCard';
import CardHeader from '@components/presentational/Cards/Parts/CardHeader';
import CardBody from '@components/presentational/Cards/Parts/CardBody';
import { RichText } from '@components/presentational/RichText';
import useUser from '@/hooks/user';

const Card = ({ title, children }) => {
  return (
    <SimpleCard width="100%" mt="0" flexGrow={0}>
      <CardHeader title={title} />
      <CardBody>{children}</CardBody>
    </SimpleCard>
  );
};

export default function ShowUserProfile({ user }: { user: User }) {
  const currentUser = useUser();

  const disciplines = useMemo<string[]>(() => {
    return user.disciplines.map(
      discipline => userTranslations['discipline'][discipline] as string
    );
  }, [userTranslations, user.disciplines]);

  const schoolType = useMemo<string>(() => {
    return userTranslations['school_type'][user.schoolType] as string;
  }, [userTranslations, user.schoolType]);

  return (
    <SimpleGrid templateColumns={{ base: '1fr', sm: '2fr 1fr' }} gap="1rem" mt="1rem">
      <Flex direction="column" gap="1rem">
        {user.introduction && user.introduction.length > 0 && (
          <Box pb={{ sm: '1rem' }}>
            <RichText html={{ __html: user.introduction }} />
          </Box>
        )}

        {disciplines?.length > 0 && (
          <Card title="Disciplines">
            <List as={Flex} gap="0.5rem" wrap="wrap">
              {disciplines.map(discipline => (
                <Tag as={ListItem} bg="brand.10" borderColor="brand.100">
                  <TagLabel lineHeight="1.25rem">{discipline}</TagLabel>
                </Tag>
              ))}
            </List>
          </Card>
        )}
        <Card title="Établissement">
          <Flex direction="column" gap="0.25rem">
            {user.school && user.school.length > 0 && (
              <Flex
                direction={{ base: 'column', sm: 'revert' }}
                align={{ sm: 'center' }}
                gap={{ sm: '0.5rem' }}
              >
                <Text fontWeight="600">
                  Nom de l'établissement principal&nbsp;:
                </Text>
                <Text>{user.school}</Text>
              </Flex>
            )}
            {user.schoolType && user.schoolType.length > 0 && (
              <Flex
                direction={{ base: 'column', sm: 'revert' }}
                align={{ sm: 'center' }}
                gap={{ sm: '0.5rem' }}
              >
                <Text fontWeight="600">Type d’établissement&nbsp;: </Text>
                <Text>{schoolType}</Text>
              </Flex>
            )}
            {user.town && user.town.length > 0 && (
              <Flex
                direction={{ base: 'column', sm: 'revert' }}
                gap={{ sm: '0.5rem' }}
              >
                <Text fontWeight="600">Adresse&nbsp;: </Text>
                <Text>
                  {user.address}, {user.postcode} {user.town}
                </Text>
              </Flex>
            )}
          </Flex>
        </Card>
      </Flex>

      <Flex direction="column" gap="1rem">
        {(user.isColleague || user.id === currentUser.id) &&
          user.publicEmail &&
          user.publicEmail.length > 0 && (
            <Card title="Adresse mail de contact">
              <Text>{user.publicEmail}</Text>
              {user.id === currentUser.id && (
                <Text fontStyle="italic" fontSize="0.75rem" pt="0.25rem">
                  Cette information est visible uniquement par vos collègues.
                </Text>
              )}
            </Card>
          )}
        {user.websites?.length > 0 && (
          <Card title="Liens">
            <List>
              {user.websites.map(website => (
                <ListItem>
                  <Link
                    href={website.url}
                    isExternal
                    textDecoration="underline"
                  >
                    {website.title}
                  </Link>
                </ListItem>
              ))}
            </List>
          </Card>
        )}
      </Flex>
    </SimpleGrid>
  );
}
